import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Howl } from 'howler';
import { useAuth } from '../features/auth';
import { useGacha } from '../features/gacha';
import { useCheckout } from '../features/checkout';
import { useLocker } from '../features/locker';
import { useToast } from '../features/toast';
import TokenIcon from './TokenIcon';
import { WELCOME_MACHINE_TOKEN_ICON, isWelcomeMachine } from '../constants/welcomeMachine';

const MACHINE_STYLE = {
  brandColor: '#C12127',
  accent: '#21476B',
  hopperCapsules: [
    ['#F5F8FF', '#F0463D'],
    ['#F5F8FF', '#2BA4B4'],
    ['#F5F8FF', '#F6C34E'],
    ['#F5F8FF', '#2BA4B4'],
    ['#F5F8FF', '#F0463D'],
    ['#F5F8FF', '#F6C34E'],
  ],
  dropCapsule: ['#F5F8FF', '#2BA4B4'],
};

const BRAND_COLORS = {
  BANDAI: '#ee161f',
  'TAKARA TOMY': '#0e51a6',
  STASTO: '#f15928',
  BUSHIROAD: '#05923c',
};

const getBrandColor = (brand) => {
  if (!brand) {
    return MACHINE_STYLE.brandColor;
  }
  const normalizedBrand = brand.trim().toUpperCase();
  return BRAND_COLORS[normalizedBrand] ?? MACHINE_STYLE.brandColor;
};

const MobileMachineModal = ({ machine, onClose, demoMode = false, demoTokens = 0, onDemoComplete }) => {
  const { user, requireLogin } = useAuth();
  const {
    startGacha,
    tryAgain,
    isSpinning,
    currentPrize: prize,
    completeAnimation,
    setPrizeModalOrigin,
    setIsPrizeModalOpen,
    isPrizeModalOpen,
    clearPrize,
  } = useGacha();
  const { registerTokenSpend, revertTokenSpend } = useCheckout();
  const { lockerDecision } = useLocker();
  const { showToast } = useToast();

  const [tokensInserted, setTokensInserted] = useState(0);
  const [activeCapsuleIndex, setActiveCapsuleIndex] = useState(0);
  const [capsuleOpened, setCapsuleOpened] = useState(false);
  const [dropComplete, setDropComplete] = useState(true);
  const [isAwaitingPrize, setIsAwaitingPrize] = useState(false);
  const [showPrizeChute, setShowPrizeChute] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const [demoPrize, setDemoPrize] = useState(null);
  const [isDemoSpinning, setIsDemoSpinning] = useState(false);
  const isOpen = Boolean(machine);

  // DEMO MODE: Compute effective values early for use in useEffect hooks
  const effectiveIsSpinning = demoMode ? isDemoSpinning : isSpinning;
  const effectivePrize = demoMode ? demoPrize : prize;

  const revealTimeoutRef = useRef(null);
  const prizeDisplayRef = useRef(null);
  const spinSoundRef = useRef(null);
  const coinSoundRef = useRef(null);
  const heartbeatSoundRef = useRef(null);
  const revealSoundRef = useRef(null);
  const dropSoundRef = useRef(null);
  const spinSequenceRef = useRef(0);
  const previousMachineRef = useRef(null);
  const swipeStartRef = useRef(null);

  const hopperCapsules = useMemo(() => MACHINE_STYLE.hopperCapsules, []);
  const dropCapsule = useMemo(() => MACHINE_STYLE.dropCapsule, []);
  const capsuleStyle = useMemo(
    () => hopperCapsules[activeCapsuleIndex] ?? dropCapsule,
    [activeCapsuleIndex, hopperCapsules, dropCapsule]
  );

  useEffect(() => {
    try {
      spinSoundRef.current = new Howl({
        src: ['/audio/spinsound.mp3'],
        volume: 0.7,
        html5: true,
      });
    } catch {}
    return () => {
      try {
        spinSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      coinSoundRef.current = new Howl({
        src: ['/audio/coinsound.mp3'],
        volume: 0.6,
        html5: true,
      });
    } catch {}
    return () => {
      try {
        coinSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      heartbeatSoundRef.current = new Howl({
        src: ['/audio/heartbeatsound.mp3'],
        volume: 0.5,
        html5: true,
        loop: true,
      });
    } catch {}
    return () => {
      try {
        heartbeatSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      revealSoundRef.current = new Howl({
        src: ['/audio/revealsound.mp3'],
        volume: 0.7,
        html5: true,
      });
    } catch {}
    return () => {
      try {
        revealSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    try {
      dropSoundRef.current = new Howl({
        src: ['/audio/dropsound.mp3'],
        volume: 0.6,
        html5: true,
      });
    } catch {}
    return () => {
      try {
        dropSoundRef.current?.unload();
      } catch {}
    };
  }, []);

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const prevMachineId = previousMachineRef.current;
    const currentMachineId = machine?.id ?? null;

    if (prevMachineId && prevMachineId !== currentMachineId && tokensInserted > 0) {
      revertTokenSpend(tokensInserted);
      setTokensInserted(0);
    }

    if (!currentMachineId) {
      setCapsuleOpened(false);
      setDropComplete(true);
      setIsAwaitingPrize(false);
      setShowPrizeChute(false);
      try {
        heartbeatSoundRef.current?.stop();
      } catch {}
      // CHANGED: Clear prize when machine is closed
      // If lockerDecision exists, the prize has become pendingPrize, clear currentPrize
      if (prize && !isPrizeModalOpen) {
        clearPrize();
      }
    } else if (prevMachineId !== currentMachineId) {
      setCapsuleOpened(false);
      setDropComplete(true);
      setIsAwaitingPrize(false);
      setShowPrizeChute(false);
      // CHANGED: Clear prize when switching machines to prevent old capsule from showing on new machine
      // If lockerDecision exists, the prize has become pendingPrize, clear currentPrize
      if (prize && !isPrizeModalOpen) {
        clearPrize();
      }
    }

    previousMachineRef.current = currentMachineId;
  }, [machine, tokensInserted, revertTokenSpend, prize, lockerDecision, isPrizeModalOpen, clearPrize]);

  useEffect(() => {
    if (isOpen) {
      return;
    }
    if (tokensInserted > 0) {
      revertTokenSpend(tokensInserted);
    }
    setTokensInserted(0);
    setCapsuleOpened(false);
    setDropComplete(true);
    setIsAwaitingPrize(false);
    setShowPrizeChute(false);
    try {
      heartbeatSoundRef.current?.stop();
    } catch {}
    
    // CHANGED: Clear prize when modal closes
    // If lockerDecision exists, the prize has become pendingPrize and should be handled via LockerFullModal, so clear currentPrize
    // isPrizeModalOpen means user is viewing prize modal, don't clear
    if (prize && !isPrizeModalOpen) {
      clearPrize();
    }
  }, [isOpen, tokensInserted, revertTokenSpend, prize, lockerDecision, isPrizeModalOpen, clearPrize]);

  useEffect(() => {
    if (isSpinning) {
      setActiveCapsuleIndex(Math.floor(Math.random() * hopperCapsules.length));
    }
  }, [isSpinning, hopperCapsules]);

  useEffect(() => {
    if (typeof document === 'undefined' || !isOpen) {
      return undefined;
    }
    const { body } = document;
    if (!body) {
      return undefined;
    }
    const previousOverflow = body.style.overflow;
    const previousTouchAction = body.style.touchAction;
    const hadClass = body.classList.contains('mobile-machine-modal-open');
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    if (!hadClass) {
      body.classList.add('mobile-machine-modal-open');
    }
    return () => {
      body.style.overflow = previousOverflow;
      body.style.touchAction = previousTouchAction;
      body.classList.remove('mobile-machine-modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!machine) {
      return;
    }
    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    const currentPrize = demoMode ? demoPrize : prize;
    if (currentPrize) {
      setCapsuleOpened(false);
      // Do not set dropComplete here: only handleDropAnimationEnd should set it when the drop
      // animation actually ends. Otherwise welcome (fast API) switches to chute before the
      // drop has finished, causing a visible gap/break between the two capsules.
      setIsAwaitingPrize(false);
      setShowPrizeChute(true);
    } else if (!isAwaitingPrize) {
      setCapsuleOpened(false);
      // DEMO MODE: Keep prize chute visible after drop completes, even without prize
      if (!demoMode) {
        setShowPrizeChute(false);
      }
      try {
        heartbeatSoundRef.current?.stop();
      } catch {}
    }
  }, [machine, prize, demoPrize, isAwaitingPrize, demoMode]);
  
  // DEMO MODE: Show prize chute after drop completes (before clicking)
  useEffect(() => {
    if (demoMode && dropComplete && !demoPrize && !isAwaitingPrize) {
      setShowPrizeChute(true);
    }
  }, [demoMode, dropComplete, demoPrize, isAwaitingPrize]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!effectiveIsSpinning && !effectivePrize) {
      setDropComplete(true);
    }
  }, [effectiveIsSpinning, effectivePrize]);

  useEffect(() => {
    // CHANGED: When modal is closed, always stop heartbeat so it never continues after onClose
    if (!isOpen) {
      try {
        heartbeatSoundRef.current?.stop();
      } catch {}
      return;
    }
    const currentPrize = demoMode ? demoPrize : prize;
    // DEMO MODE: Jitter even without prize (prize will be set on click)
    const shouldJitter = showPrizeChute && dropComplete && (demoMode ? true : currentPrize) && !capsuleOpened && !isPrizeModalOpen;
    try {
      const heartbeatSound = heartbeatSoundRef.current;
      if (heartbeatSound) {
        if (shouldJitter) {
          if (!heartbeatSound.playing()) {
            heartbeatSound.play();
          }
        } else if (heartbeatSound.playing()) {
          heartbeatSound.stop();
        }
      }
    } catch {}
  }, [isOpen, showPrizeChute, dropComplete, prize, demoPrize, capsuleOpened, isPrizeModalOpen, demoMode]);

  const hasMachineImage = Boolean(machine?.imageUrl);
  const machineSoldOut = demoMode ? false : (machine ? machine.inventoryAvailable <= 0 : false);
  const userTokens = typeof user?.tokens === 'number' ? user.tokens : 0;
  const availableTokens = demoMode ? demoTokens : userTokens;
  const isFullyInserted = machine ? tokensInserted >= machine.tokenCost : false;

  // First spin discount eligibility check
  // CHANGED: Remove token check from knobDisabled - show toast on click instead
  const knobDisabled = Boolean(
    machineSoldOut ||
      !isFullyInserted
  );
  const tokensNeeded = machine ? machine.tokenCost - tokensInserted : 0;
  // CHANGED: Remove token check from coinSlotDisabled - show toast on click instead
  const coinSlotDisabled = Boolean(
    effectiveIsSpinning ||
      machineSoldOut ||
      isFullyInserted ||
      !dropComplete ||
      tokensNeeded <= 0
  );

  const knobAxisClassNames = ['machine-knob-axis'];
  if (effectiveIsSpinning) {
    knobAxisClassNames.push('is-rotating');
  }
  const knobButtonClassNames = ['machine-knob'];
  if (knobDisabled || effectiveIsSpinning) {
    knobButtonClassNames.push('is-disabled');
  }
  if (isFullyInserted && !effectiveIsSpinning) {
    knobButtonClassNames.push('is-armed');
  }

  const coinSlotClassNames = ['machine-coin-slot'];
  if (coinSlotDisabled) {
    coinSlotClassNames.push('is-disabled');
  }
  if (machineSoldOut) {
    coinSlotClassNames.push('sold-out');
  }
  if (isFullyInserted) {
    coinSlotClassNames.push('token-inserted');
  }
  const hasCapsuleWaiting = showPrizeChute && dropComplete && (demoMode ? true : prize) && !capsuleOpened;
  // DEMO MODE: Disable nudge-shake animation when arrow is showing
  if (!coinSlotDisabled && !isFullyInserted && !hasCapsuleWaiting && !demoMode) {
    coinSlotClassNames.push('nudge-shake');
  }

  const handleInsertToken = useCallback(() => {
    if (!machine || effectiveIsSpinning || machineSoldOut || !dropComplete) {
      return;
    }
    
    // DEMO MODE: Skip user checks
    if (!demoMode) {
      if (!user) {
        if (typeof requireLogin === 'function') {
          requireLogin();
        }
        return;
      }
      // CHANGED: Check token balance and show toast instead of silently failing
      if (user && machine && userTokens < machine.tokenCost) {
        showToast({
          message: 'Not enough tokens to spin!',
          tone: 'error',
        });
        return;
      }
      if (user?.pendingPrize) {
        if (lockerDecision?.pendingPrize) {
          setPrizeModalOrigin(null);
          setIsPrizeModalOpen(true);
        }
        // CHANGED: Removed toast - LockerFullModal will be shown instead
        return;
      }
    }

    const tokensRemaining = machine.tokenCost - tokensInserted;
    if (tokensRemaining <= 0) {
      return;
    }
    // DEMO MODE: Skip token availability check
    if (!demoMode && availableTokens < tokensInserted + 1) {
      showToast({
        message: 'Not enough tokens to insert.',
        tone: 'error',
      });
      return;
    }

    setCapsuleOpened(false);
    const newTokensInserted = tokensInserted + 1;
    setTokensInserted(newTokensInserted);
    
    // DEMO MODE: Don't call registerTokenSpend
    if (!demoMode) {
      registerTokenSpend(1);
    }

    try {
      const coinSound = coinSoundRef.current;
      if (coinSound) {
        if (coinSound.playing()) {
          coinSound.stop();
        }
        coinSound.play();
      }
    } catch {}
  }, [
    machine,
    effectiveIsSpinning,
    machineSoldOut,
    dropComplete,
    user,
    requireLogin,
    lockerDecision,
    setPrizeModalOrigin,
    setIsPrizeModalOpen,
    showToast,
    tokensInserted,
    availableTokens,
    registerTokenSpend,
    demoMode,
    userTokens,
  ]);

  const handleKnobClick = useCallback(() => {
    if (!machine || knobDisabled || effectiveIsSpinning) {
      return;
    }
    
    // DEMO MODE: Skip user checks
    if (!demoMode) {
      if (!user) {
        if (typeof requireLogin === 'function') {
          requireLogin();
        }
        return;
      }
      // CHANGED: Check token balance and show toast instead of silently failing
      if (user && machine && userTokens < machine.tokenCost) {
        showToast({
          message: 'Not enough tokens to spin!',
          tone: 'error',
        });
        return;
      }
    }
    
    if (!isFullyInserted) {
      return;
    }

    try {
      const spinSound = spinSoundRef.current;
      if (spinSound) {
        if (spinSound.playing()) {
          spinSound.stop();
        }
        spinSound.play();
      }
    } catch {}

    setTokensInserted(0);
    setCapsuleOpened(false);
    setIsAwaitingPrize(true);
    setShowPrizeChute(true);
    setDropComplete(false);
    setTimeout(() => {
      try {
        const dropSound = dropSoundRef.current;
        if (dropSound) {
          if (dropSound.playing()) {
            dropSound.stop();
          }
          dropSound.play();
        }
      } catch {}
    }, 1100);

    // DEMO MODE: Set demo spinning state instead of calling API
    if (demoMode) {
      setIsDemoSpinning(true);
    } else {
      const config = { machineId: machine.id, tokenCost: machine.tokenCost };
      spinSequenceRef.current += 1;
      if (prize) {
        tryAgain(config);
      } else {
        startGacha(config);
      }
    }
  }, [machine, knobDisabled, effectiveIsSpinning, user, requireLogin, isFullyInserted, prize, tryAgain, startGacha, userTokens, showToast, demoMode]);

  const handleCapsuleOpen = useCallback(() => {
    // DEMO MODE: Set demo prize when capsule is clicked
    if (demoMode && !demoPrize) {
      // Create a demo prize from machine's prize pool
      const demoPrizeData = {
        id: 'demo-prize-' + Date.now(),
        name: machine?.prizePool?.[0]?.name || 'Demo Prize',
        imageUrl: machine?.prizePool?.[0]?.imageUrl || '/assets/ponpon-logo.svg',
        image: machine?.prizePool?.[0]?.image || '🎁',
      };
      setDemoPrize(demoPrizeData);
    }
    
    const currentPrize = demoMode ? demoPrize : prize;
    if (!capsuleOpened && currentPrize) {
      try {
        heartbeatSoundRef.current?.stop();
      } catch {}
      try {
        const revealSound = revealSoundRef.current;
        if (revealSound) {
          if (revealSound.playing()) {
            revealSound.stop();
          }
          revealSound.play();
        }
      } catch {}
      setCapsuleOpened(true);
      setShowFireworks(true);
      setTimeout(() => setShowFireworks(false), 1300);
      const invokeReveal = () => {
        const rect = prizeDisplayRef.current
          ? prizeDisplayRef.current.getBoundingClientRect()
          : null;
        if (rect) {
          setPrizeModalOrigin({
            x: rect.left + rect.width / 2 - window.innerWidth / 2,
            y: rect.top + rect.height / 2 - window.innerHeight / 2,
          });
        } else {
          setPrizeModalOrigin(null);
        }
        const currentPrize = demoMode ? demoPrize : prize;
        if (currentPrize && !demoMode) {
          setIsPrizeModalOpen(true);
        }
        // DEMO MODE: Call onDemoComplete after reveal
        if (demoMode && currentPrize) {
          setTimeout(() => {
            onDemoComplete?.(currentPrize);
          }, 500);
        }
      };
      setTimeout(() => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(invokeReveal);
        } else {
          invokeReveal();
        }
      }, 950);
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }
      revealTimeoutRef.current = setTimeout(() => {
        setCapsuleOpened(false);
        setShowPrizeChute(false);
        setIsAwaitingPrize(false);
        revealTimeoutRef.current = null;
      }, 1000);
    }
  }, [capsuleOpened, prize, demoPrize, setPrizeModalOrigin, setIsPrizeModalOpen, demoMode, machine, onDemoComplete]);

  const handleSwipeStart = useCallback((event) => {
    swipeStartRef.current = event.touches?.[0]?.clientY ?? null;
  }, []);

  const resetSwipe = useCallback(() => {
    swipeStartRef.current = null;
  }, []);

  const handleSwipeEnd = useCallback(
    (event) => {
      const startY = swipeStartRef.current;
      const endY = event.changedTouches?.[0]?.clientY ?? null;
      resetSwipe();
      if (typeof startY === 'number' && typeof endY === 'number' && startY - endY >= 40) {
        onClose?.();
      }
    },
    [onClose, resetSwipe]
  );

  const handleDropAnimationEnd = useCallback(
    (event) => {
      if (event && event.target !== event.currentTarget) {
        return;
      }
      setDropComplete(true);
      setIsAwaitingPrize(true);
      
      // DEMO MODE: Just stop spinning, prize will be set on capsule click
      if (demoMode) {
        setIsDemoSpinning(false);
      } else {
        completeAnimation({
          machineId: machine?.id,
          tokenCost: machine?.tokenCost,
        });
      }
    },
    [machine, completeAnimation, demoMode]
  );

  if (!machine) {
    return null;
  }

  return (
    <div className={`mobile-machine-modal fixed ${demoMode ? 'inset-0' : 'inset-x-0 top-[var(--mobile-header-offset,3.25rem)] bottom-0'} z-50 flex flex-col bg-white`}>
      <div className="mobile-machine-layout flex h-full flex-col px-0 pt-0 sm:px-0 sm:pt-0">
        <div className={`mobile-machine-stage flex w-full justify-center ${demoMode ? 'pt-0' : 'pt-2'}`}>
          <section className="machine-modal-dialog mobile-machine-dialog w-full max-w-md">
            <div className="machine-unit machine-unit-large relative mx-auto">
              <div className="machine-top" style={{ borderColor: `${MACHINE_STYLE.accent}33` }}>
                <div className="machine-brand" style={{ color: getBrandColor(machine.brand) }}>
                  {machine.brand || 'PonPon'}
                </div>
              </div>
              <div className="machine-glass">
                {hasMachineImage ? (
                  <div className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70">
                    <img
                      src={machine.imageUrl}
                      alt={machine.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="machine-primary-image flex h-full w-full items-center justify-center bg-white/70">
                    <div className="text-6xl">🎁</div>
                  </div>
                )}
              </div>
              <div className="machine-body-shell">
                <button
                  type="button"
                  onClick={handleInsertToken}
                  disabled={coinSlotDisabled}
                  className="machine-insert-panel relative"
                  aria-label={
                    machineSoldOut
                      ? 'Machine is sold out'
                      : isFullyInserted
                      ? `All ${machine.tokenCost} token${machine.tokenCost === 1 ? '' : 's'} inserted`
                      : `Insert token ${tokensInserted + 1} of ${machine.tokenCost}`
                  }
                  aria-pressed={isFullyInserted}
                >
                  {demoMode && !isFullyInserted && !showPrizeChute && (
                    <span className="absolute -left-10 top-1/2 -translate-y-1/2 text-ponpon-coral animate-bounce z-10" style={{ fontSize: '2.5rem' }}>
                      👉
                    </span>
                  )}
                  <div className="machine-label" style={{ color: MACHINE_STYLE.accent }}>
                    Insert Token
                  </div>
                  <div className={coinSlotClassNames.join(' ')}>
                    {!machineSoldOut && !isFullyInserted && (
                      <span className="machine-coin-icon" aria-hidden="true">
                        <TokenIcon className="!w-[1.15rem] !h-[1.15rem] !min-w-[1.15rem] !min-h-[1.15rem]" alt="" />
                      </span>
                    )}
                    <span className="machine-token-text">
                      {machineSoldOut ? (
                        <strong>Sold out</strong>
                      ) : isFullyInserted ? (
                        <strong>Tap →</strong>
                      ) : (
                        <strong>{tokensInserted}/{machine.tokenCost}</strong>
                      )}
                    </span>
                  </div>
                  <div className="machine-slot-rails" aria-hidden="true">
                    <span className="slot-rail slot-rail-left" />
                    <span className="slot-rail slot-rail-right" />
                  </div>
                </button>
                <div className={knobAxisClassNames.join(' ')}>
                  <button
                    type="button"
                    onClick={handleKnobClick}
                    disabled={knobDisabled || effectiveIsSpinning}
                    className={knobButtonClassNames.join(' ')}
                    aria-label="Turn dial to spin the machine"
                  >
                    <span className="machine-knob-face" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="machine-chute" aria-hidden="true">
                {/* Only show falling capsule while drop is in progress; once dropComplete, only chute-ready shows */}
                {!dropComplete && (
                  <div
                    className="active-drop-capsule"
                    style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}
                    onAnimationEnd={handleDropAnimationEnd}
                  >
                    <div className="active-drop-shell" />
                  </div>
                )}
                {showPrizeChute && dropComplete && (!effectiveIsSpinning || isAwaitingPrize) ? (
                  <div
                    className={`chute-ready ${capsuleOpened ? 'opened' : ''} relative`}
                    style={{ '--capsule-top': capsuleStyle[0], '--capsule-bottom': capsuleStyle[1] }}
                  >
                    {demoMode && !capsuleOpened && (
                      <span className="absolute -left-3 top-1/4 -translate-y-1/2 text-ponpon-coral animate-bounce z-10" style={{ fontSize: '2.5rem' }}>
                        👉
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={handleCapsuleOpen}
                      disabled={(!demoMode && !effectivePrize) || capsuleOpened}
                      className={`chute-capsule-trigger ${capsuleOpened ? 'opened' : ''}`}
                    >
                      <div
                        className={`chute-capsule-shell ${capsuleOpened ? 'open' : ''} ${
                          !capsuleOpened && (demoMode || effectivePrize) ? 'capsule-jitter' : ''
                        }`}
                      />
                    </button>
                    {capsuleOpened && effectivePrize ? (
                      <div className="chute-prize-display reveal" ref={prizeDisplayRef}>
                        {(typeof effectivePrize?.imageUrl === 'string' && effectivePrize.imageUrl.trim().length > 0) ||
                        effectivePrize?.tokenBonus ||
                        isWelcomeMachine(machine) ? (
                          <img
                            src={effectivePrize?.imageUrl || WELCOME_MACHINE_TOKEN_ICON}
                            alt={effectivePrize.name || 'Prize'}
                            className="max-h-24 max-w-[70%] object-contain"
                          />
                        ) : (
                          <span className="text-4xl sm:text-5xl">{effectivePrize?.image || '🎁'}</span>
                        )}
                      </div>
                    ) : null}
                    {showFireworks ? (
                      <div className="fireworks" aria-hidden="true">
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                        <span className="spark" />
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        </div>
        <button
          type="button"
          onTouchStart={handleSwipeStart}
          onTouchEnd={handleSwipeEnd}
          onTouchCancel={resetSwipe}
          onClick={onClose}
          className="mobile-machine-close flex w-full flex-col items-center justify-center pt-5 pb-5 text-sm font-semibold text-ponpon-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-ponpon-coral/50"
        >
          <span className="mobile-machine-close-handle mb-1 h-1.5 w-10 rounded-full" />
          <span>Tap to close</span>
        </button>
      </div>
    </div>
  );
};

export default MobileMachineModal;
